import React, { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import DaySection from './DaySection';
import CalltimeSection from './CalltimeSection';
import CrewInfoSection from './CrewInfoSection';
import TextBlockSection from './TextBlockSection';
import DayCardMobile from './DayCardMobile';
import CalltimeCardMobile from './CalltimeCardMobile';
import { Button } from './ui/button';
import { Plus } from 'lucide-react';

// Schwebende Vorschau der gezogenen Zeile (im DragOverlay)
function RowOverlay({ row, columns }) {
  if (!row) return null;
  if (row.type === 'text') {
    return (
      <div className="bg-white shadow-xl border-2 border-blue-500 rounded px-4 py-2 text-sm font-semibold text-slate-800 opacity-95">
        {row.text != null ? row.text : (row.notes || '—')}
      </div>
    );
  }
  const cols = columns || [];
  return (
    <div className="bg-white shadow-xl border-2 border-blue-500 rounded px-3 py-2 text-sm text-slate-700 opacity-95 flex gap-3 items-center">
      {cols.map((c) => (
        <span key={c.id} className="truncate" style={{ maxWidth: 160 }}>
          {row[c.id] || <span className="text-slate-300">–</span>}
        </span>
      ))}
    </div>
  );
}

// Schwebende Vorschau eines ganzen Blocks (Tag / Calltime / CrewInfo / Notiz)
function SectionOverlay({ item }) {
  if (!item) return null;
  const label =
    item.itemType === 'day'
      ? (item.date || 'Tag')
      : item.itemType === 'textBlock'
      ? 'Notiz'
      : (item.title || 'Block');
  const accent = item.color || '#2563eb';
  return (
    <div
      className="bg-white shadow-2xl rounded-lg px-4 py-3 font-semibold text-slate-800 opacity-95"
      style={{ minWidth: 260, border: '2px solid #3b82f6', borderLeft: `6px solid ${accent}` }}
    >
      {label}
    </div>
  );
}

function ScheduleEditor({ project, onProjectChange }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Für die Drag-Vorschau (schwebende Zeile / schwebender Block)
  const [activeRow, setActiveRow] = useState(null);
  const [activeSection, setActiveSection] = useState(null);

  // Combine days, calltimes and crewInfos into a single sortable list, sorted by position
  const allItems = [
    ...project.days.map(day => ({ ...day, itemType: 'day' })),
    ...(project.calltimes || []).map(ct => ({ ...ct, itemType: 'calltime' })),
    ...(project.crewInfos || []).map(ci => ({ ...ci, itemType: 'crewInfo' })),
    ...(project.textBlocks || []).map(tb => ({ ...tb, itemType: 'textBlock' }))
  ].sort((a, b) => (a.position || 0) - (b.position || 0));

  // Hilfsfunktion: aktuelle Listen (in Anzeige-Reihenfolge) aus allItems lösen
  const splitItems = (items) => ({
    days: items.filter((i) => i.itemType === 'day').map(({ itemType, ...r }) => r),
    calltimes: items.filter((i) => i.itemType === 'calltime').map(({ itemType, ...r }) => r),
    crewInfos: items.filter((i) => i.itemType === 'crewInfo').map(({ itemType, ...r }) => r),
    textBlocks: items.filter((i) => i.itemType === 'textBlock').map(({ itemType, ...r }) => r),
  });

  const handleDragStart = (event) => {
    const { active } = event;
    const t = active.data.current?.type;
    if (t === 'row') {
      const dayId = active.data.current.dayId;
      const day = (project.days || []).find((d) => d.id === dayId);
      const row = day?.rows.find((r) => r.id === active.id);
      setActiveRow(row || null);
      setActiveSection(null);
    } else if (t === 'section') {
      setActiveSection(allItems.find((i) => i.id === active.id) || null);
      setActiveRow(null);
    } else {
      setActiveRow(null);
      setActiveSection(null);
    }
  };

  const clearActive = () => {
    setActiveRow(null);
    setActiveSection(null);
  };

  // Live-Verschieben: zieht eine Zeile sichtbar schon während des Ziehens in den Ziel-Tag.
  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;
    if (active.data.current?.type !== 'row') return;

    const activeDayId = active.data.current.dayId;
    const oType = over.data.current?.type;

    let overDayId = null;
    if (oType === 'row' || oType === 'container') overDayId = over.data.current?.dayId;
    else {
      const s = allItems.find((i) => i.id === over.id);
      if (s && s.itemType === 'day') overDayId = over.id;
    }
    if (!overDayId || overDayId === activeDayId) return; // gleiches Tag -> erst in onDragEnd sortieren

    const { days: currentDays, calltimes, crewInfos } = splitItems(allItems);
    const sourceDay = currentDays.find((d) => d.id === activeDayId);
    const targetDay = currentDays.find((d) => d.id === overDayId);
    if (!sourceDay || !targetDay) return;
    const movingRow = sourceDay.rows.find((r) => r.id === active.id);
    if (!movingRow) return;

    let insertIndex = targetDay.rows.length;
    if (oType === 'row') {
      const idx = targetDay.rows.findIndex((r) => r.id === over.id);
      if (idx >= 0) insertIndex = idx;
    }

    const newSourceRows = sourceDay.rows.filter((r) => r.id !== active.id);
    const newTargetRows = [...targetDay.rows];
    newTargetRows.splice(insertIndex, 0, movingRow);
    const newDays = currentDays.map((d) => {
      if (d.id === activeDayId) return { ...d, rows: newSourceRows };
      if (d.id === overDayId) return { ...d, rows: newTargetRows };
      return d;
    });

    // Aktive Zeile gehört ab jetzt zum Ziel-Tag (für Folge-Events / onDragEnd)
    active.data.current.dayId = overDayId;
    onProjectChange({ ...project, days: newDays, calltimes, crewInfos });
  };

  const handleDragEnd = (event) => {
    clearActive();
    const { active, over } = event;
    if (!over) return;

    const aType = active.data.current?.type;
    const oType = over.data.current?.type;

    // ---------- 1) Sektionen (Tage / Calltimes / Crew-Infos) umsortieren ----------
    if (aType === 'section') {
      // Ziel-Sektion bestimmen – auch wenn über einer Zeile/einem Tag-Container losgelassen wurde
      let overSectionId = over.id;
      if (oType === 'row' || oType === 'container') overSectionId = over.data.current?.dayId;

      const oldIndex = allItems.findIndex((i) => i.id === active.id);
      const newIndex = allItems.findIndex((i) => i.id === overSectionId);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

      const reordered = arrayMove(allItems, oldIndex, newIndex).map((item, index) => ({
        ...item,
        position: index,
      }));
      const { days, calltimes, crewInfos, textBlocks } = splitItems(reordered);
      onProjectChange({ ...project, days, calltimes, crewInfos, textBlocks });
      return;
    }

    // ---------- 2) Zeilen innerhalb / zwischen Tagen verschieben ----------
    if (aType === 'row') {
      const sourceDayId = active.data.current?.dayId;
      if (!sourceDayId) return;

      // Ziel-Tag ermitteln (Zeile, Tag-Container, oder direkt der Tag-Header)
      let targetDayId = null;
      if (oType === 'row' || oType === 'container') {
        targetDayId = over.data.current?.dayId;
      } else {
        const overSection = allItems.find((i) => i.id === over.id);
        if (overSection && overSection.itemType === 'day') targetDayId = over.id;
      }
      if (!targetDayId) return;

      const { days: currentDays, calltimes, crewInfos } = splitItems(allItems);
      const sourceDay = currentDays.find((d) => d.id === sourceDayId);
      const targetDay = currentDays.find((d) => d.id === targetDayId);
      if (!sourceDay || !targetDay) return;
      const movingRow = sourceDay.rows.find((r) => r.id === active.id);
      if (!movingRow) return;

      let newDays;
      if (sourceDayId === targetDayId) {
        // innerhalb desselben Tages
        const oldIndex = sourceDay.rows.findIndex((r) => r.id === active.id);
        const overIndex =
          oType === 'row' ? sourceDay.rows.findIndex((r) => r.id === over.id) : sourceDay.rows.length - 1;
        if (oldIndex === -1 || overIndex === -1 || oldIndex === overIndex) return;
        const reordered = arrayMove(sourceDay.rows, oldIndex, overIndex);
        newDays = currentDays.map((d) => (d.id === sourceDayId ? { ...d, rows: reordered } : d));
      } else {
        // in einen anderen Tag
        const insertIndex =
          oType === 'row' ? targetDay.rows.findIndex((r) => r.id === over.id) : targetDay.rows.length;
        const newSourceRows = sourceDay.rows.filter((r) => r.id !== active.id);
        const newTargetRows = [...targetDay.rows];
        newTargetRows.splice(insertIndex < 0 ? newTargetRows.length : insertIndex, 0, movingRow);
        newDays = currentDays.map((d) => {
          if (d.id === sourceDayId) return { ...d, rows: newSourceRows };
          if (d.id === targetDayId) return { ...d, rows: newTargetRows };
          return d;
        });
      }
      onProjectChange({ ...project, days: newDays, calltimes, crewInfos });
      return;
    }
  };

  const handleAddDay = () => {
    const today = new Date();
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
    
    const newDay = {
      id: Date.now().toString(),
      date: formattedDate,
      position: allItems.length,
      icon: 'calendar',
      rows: [
        {
          id: Date.now().toString() + '-1',
          type: 'item',
          time: '',
          scene: '',
          location: '',
          cast: '',
          notes: ''
        }
      ]
    };

    onProjectChange({ ...project, days: [...project.days, newDay] });
  };

  const handleAddCalltime = () => {
    const newCalltime = {
      id: Date.now().toString(),
      title: 'Calltime',
      headers: {
        time: 'Time',
        name: 'Name'
      },
      columns: [
        { id: 'time', label: 'Time', width: 18 },
        { id: 'name', label: 'Name', width: 82 },
      ],
      position: allItems.length,
      icon: 'clock',
      rows: [
        {
          id: Date.now().toString() + '-1',
          type: 'item',
          time: '',
          name: ''
        }
      ]
    };

    onProjectChange({ ...project, calltimes: [...(project.calltimes || []), newCalltime] });
  };

  const handleAddCrewInfo = () => {
    const newCrewInfo = {
      id: Date.now().toString(),
      title: 'Crew Info',
      headers: {
        name: 'Name',
        crew: 'Crew',
        info: 'Info'
      },
      columns: [
        { id: 'name', label: 'Name', width: 25 },
        { id: 'crew', label: 'Crew', width: 20 },
        { id: 'info', label: 'Info', width: 55 },
      ],
      position: allItems.length,
      icon: 'phone',
      rows: [
        {
          id: Date.now().toString() + '-1',
          type: 'item',
          name: '',
          crew: '',
          info: ''
        }
      ]
    };

    onProjectChange({ ...project, crewInfos: [...(project.crewInfos || []), newCrewInfo] });
  };

  const handleAddTextBlock = () => {
    const newBlock = {
      id: Date.now().toString(),
      text: '',
      width: '100%',
      height: '120px',
      color: null,
      position: allItems.length,
    };
    onProjectChange({ ...project, textBlocks: [...(project.textBlocks || []), newBlock] });
  };

  const handleUpdateTextBlock = (id, updated) => {
    onProjectChange({
      ...project,
      textBlocks: (project.textBlocks || []).map((tb) => (tb.id === id ? updated : tb)),
    });
  };

  const handleRemoveTextBlock = (id) => {
    onProjectChange({
      ...project,
      textBlocks: (project.textBlocks || []).filter((tb) => tb.id !== id),
    });
  };

  const handleUpdateDay = (dayId, updatedDay) => {
    // Update using the current order from allItems
    const days = allItems
      .filter(item => item.itemType === 'day')
      .map(item => item.id === dayId ? { ...updatedDay, itemType: 'day' } : item)
      .map(({ itemType, ...rest }) => rest);
    
    const calltimes = allItems
      .filter(item => item.itemType === 'calltime')
      .map(({ itemType, ...rest }) => rest);
    
    const crewInfos = allItems
      .filter(item => item.itemType === 'crewInfo')
      .map(({ itemType, ...rest }) => rest);
    
    onProjectChange({
      ...project,
      days,
      calltimes,
      crewInfos
    });
  };

  const handleUpdateCalltime = (calltimeId, updatedCalltime) => {
    // Update using the current order from allItems
    const days = allItems
      .filter(item => item.itemType === 'day')
      .map(({ itemType, ...rest }) => rest);
    
    const calltimes = allItems
      .filter(item => item.itemType === 'calltime')
      .map(item => item.id === calltimeId ? { ...updatedCalltime, itemType: 'calltime' } : item)
      .map(({ itemType, ...rest }) => rest);
    
    const crewInfos = allItems
      .filter(item => item.itemType === 'crewInfo')
      .map(({ itemType, ...rest }) => rest);
    
    onProjectChange({
      ...project,
      days,
      calltimes,
      crewInfos
    });
  };

  const handleUpdateCrewInfo = (crewInfoId, updatedCrewInfo) => {
    // Update using the current order from allItems
    const days = allItems
      .filter(item => item.itemType === 'day')
      .map(({ itemType, ...rest }) => rest);
    
    const calltimes = allItems
      .filter(item => item.itemType === 'calltime')
      .map(({ itemType, ...rest }) => rest);
    
    const crewInfos = allItems
      .filter(item => item.itemType === 'crewInfo')
      .map(item => item.id === crewInfoId ? { ...updatedCrewInfo, itemType: 'crewInfo' } : item)
      .map(({ itemType, ...rest }) => rest);
    
    onProjectChange({
      ...project,
      days,
      calltimes,
      crewInfos
    });
  };

  const handleRemoveDay = (dayId) => {
    // Separate the items
    const days = allItems.filter(item => item.itemType === 'day' && item.id !== dayId).map(({ itemType, ...rest }) => rest);
    const calltimes = allItems.filter(item => item.itemType === 'calltime').map(({ itemType, ...rest }) => rest);
    const crewInfos = allItems.filter(item => item.itemType === 'crewInfo').map(({ itemType, ...rest }) => rest);
    onProjectChange({
      ...project,
      days,
      calltimes,
      crewInfos
    });
  };

  const handleRemoveCalltime = (calltimeId) => {
    // Separate the items
    const days = allItems.filter(item => item.itemType === 'day').map(({ itemType, ...rest }) => rest);
    const calltimes = allItems.filter(item => item.itemType === 'calltime' && item.id !== calltimeId).map(({ itemType, ...rest }) => rest);
    const crewInfos = allItems.filter(item => item.itemType === 'crewInfo').map(({ itemType, ...rest }) => rest);
    onProjectChange({
      ...project,
      days,
      calltimes,
      crewInfos
    });
  };

  const handleRemoveCrewInfo = (crewInfoId) => {
    // Separate the items
    const days = allItems.filter(item => item.itemType === 'day').map(({ itemType, ...rest }) => rest);
    const calltimes = allItems.filter(item => item.itemType === 'calltime').map(({ itemType, ...rest }) => rest);
    const crewInfos = allItems.filter(item => item.itemType === 'crewInfo' && item.id !== crewInfoId).map(({ itemType, ...rest }) => rest);
    onProjectChange({
      ...project,
      days,
      calltimes,
      crewInfos
    });
  };

  return (
    <div className="schedule-editor">
      {/* Desktop View */}
      <div className="hidden md:block">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={clearActive}
        >
          <SortableContext
            items={allItems.map(item => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {allItems.map((item) => (
              item.itemType === 'day' ? (
                <DaySection
                  key={item.id}
                  day={item}
                  columns={project.columns}
                  onUpdateDay={(updatedDay) => handleUpdateDay(item.id, updatedDay)}
                  onUpdateColumns={(cols) => onProjectChange({ ...project, columns: cols })}
                  onRemoveDay={() => handleRemoveDay(item.id)}
                  canRemove={true}
                />
              ) : item.itemType === 'calltime' ? (
                <CalltimeSection
                  key={item.id}
                  calltime={item}
                  onUpdateCalltime={(updatedCalltime) => handleUpdateCalltime(item.id, updatedCalltime)}
                  onRemoveCalltime={() => handleRemoveCalltime(item.id)}
                  canRemove={true}
                />
              ) : item.itemType === 'crewInfo' ? (
                <CrewInfoSection
                  key={item.id}
                  crewInfo={item}
                  onUpdateCrewInfo={(updatedCrewInfo) => handleUpdateCrewInfo(item.id, updatedCrewInfo)}
                  onRemoveCrewInfo={() => handleRemoveCrewInfo(item.id)}
                  canRemove={true}
                />
              ) : (
                <TextBlockSection
                  key={item.id}
                  block={item}
                  onUpdate={(u) => handleUpdateTextBlock(item.id, u)}
                  onRemove={() => handleRemoveTextBlock(item.id)}
                />
              )
            ))}
          </SortableContext>

          <DragOverlay dropAnimation={{ duration: 180 }}>
            {activeRow ? (
              <RowOverlay row={activeRow} columns={project.columns} />
            ) : activeSection ? (
              <SectionOverlay item={activeSection} />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Mobile View */}
      <div className="block md:hidden">
        {allItems.map((item) => (
          item.itemType === 'day' ? (
            <DayCardMobile
              key={item.id}
              day={item}
              columns={project.columns}
              onUpdateDay={(updatedDay) => handleUpdateDay(item.id, updatedDay)}
              onRemoveDay={() => handleRemoveDay(item.id)}
              canRemove={true}
            />
          ) : item.itemType === 'calltime' ? (
            <CalltimeCardMobile
              key={item.id}
              calltime={item}
              onUpdateCalltime={(updatedCalltime) => handleUpdateCalltime(item.id, updatedCalltime)}
              onRemoveCalltime={() => handleRemoveCalltime(item.id)}
            />
          ) : null
        ))}
      </div>

      <div className="mt-6 flex gap-3 no-print flex-wrap">
        <Button
          onClick={handleAddDay}
          variant="outline"
          className="w-full md:w-auto"
          data-testid="add-day-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Day
        </Button>
        <Button
          onClick={handleAddCalltime}
          variant="outline"
          className="w-full md:w-auto"
          data-testid="add-calltime-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Calltime
        </Button>
        <Button
          onClick={handleAddCrewInfo}
          variant="outline"
          className="w-full md:w-auto"
          data-testid="add-crew-info-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Crew Info
        </Button>
        <Button
          onClick={handleAddTextBlock}
          variant="outline"
          className="w-full md:w-auto"
          data-testid="add-textblock-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Notiz
        </Button>
      </div>
    </div>
  );
}

export default ScheduleEditor;
